import type { ThemeId, ThemePalette } from './theme';

const CUSTOM_THEME_PROPERTIES = [
  '--fp-bg-page',
  '--fp-bg-chrome',
  '--fp-bg-nav',
  '--fp-bg-surface',
  '--fp-primary',
  '--fp-primary-soft',
  '--fp-interactive-hover-bg',
  '--fp-interactive-hover-text',
  '--fp-on-primary',
  '--fp-text',
  '--fp-text-muted',
  '--fp-danger',
  '--fp-success',
  '--fp-focus',
] as const;

const FALLBACK_CUSTOM_PALETTE: ThemePalette = ['#f6efe1', '#fffaf0', '#b8896a'];

function readableTextColor(hex: string): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return '#2a2620';
  const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255);
  const linear = channels.map((channel) => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance < 0.179 ? '#ffffff' : '#000000';
}

function clearCustomThemeVariables(rootStyle: CSSStyleDeclaration): void {
  CUSTOM_THEME_PROPERTIES.forEach((property) => rootStyle.removeProperty(property));
}

/** 将主题状态映射为根节点 CSS 变量；主题状态本身由 ThemeProvider 管理。 */
export function applyThemeRuntime(theme: ThemeId, customPalette: ThemePalette | null, customBackground: string | null): void {
  const root = document.documentElement;
  const rootStyle = root.style;
  root.dataset.theme = theme;

  const palette = theme === 'custom' ? (customPalette ?? FALLBACK_CUSTOM_PALETTE) : null;
  if (palette) {
    const [backgroundColor, primaryColor, accentColor] = palette;
    const textColor = readableTextColor(backgroundColor);
    const primary = accentColor || primaryColor;
    rootStyle.setProperty('--fp-bg-page', backgroundColor);
    rootStyle.setProperty('--fp-bg-chrome', `color-mix(in srgb, ${backgroundColor} 72%, ${textColor} 28%)`);
    rootStyle.setProperty('--fp-bg-nav', `color-mix(in srgb, ${backgroundColor} 64%, ${textColor} 36%)`);
    rootStyle.setProperty('--fp-bg-surface', `color-mix(in srgb, ${backgroundColor} 82%, ${textColor} 18%)`);
    rootStyle.setProperty('--fp-primary', primary);
    rootStyle.setProperty('--fp-primary-soft', `color-mix(in srgb, ${primary} 18%, transparent)`);
    rootStyle.setProperty('--fp-interactive-hover-bg', backgroundColor);
    rootStyle.setProperty('--fp-interactive-hover-text', textColor);
    rootStyle.setProperty('--fp-on-primary', readableTextColor(primary));
    rootStyle.setProperty('--fp-text', textColor);
    rootStyle.setProperty('--fp-text-muted', `color-mix(in srgb, ${textColor} 74%, ${backgroundColor} 26%)`);
    rootStyle.setProperty('--fp-danger', `color-mix(in srgb, var(--fp-danger-base) 40%, ${textColor} 60%)`);
    rootStyle.setProperty('--fp-success', `color-mix(in srgb, var(--fp-success-base) 40%, ${textColor} 60%)`);
    rootStyle.setProperty('--fp-focus', textColor === '#ffffff' ? '#ffd166' : '#0e2547');
  } else {
    clearCustomThemeVariables(rootStyle);
  }

  if (theme === 'custom' && customBackground) {
    const backgroundColor = palette?.[0] ?? FALLBACK_CUSTOM_PALETTE[0];
    const textColor = readableTextColor(backgroundColor);
    rootStyle.setProperty('--fp-page-background', `linear-gradient(color-mix(in srgb, ${textColor} 14%, transparent), color-mix(in srgb, ${textColor} 14%, transparent)), url("${customBackground}") center / cover fixed`);
  } else {
    rootStyle.removeProperty('--fp-page-background');
  }
}
